import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useConvexAuth, useQuery } from "convex/react";
import { api } from '../../../../convex/_generated/api';
import { SignOutButton } from '../../../SignOutButton';
import { TokenBalance } from '../../../pages/tokens/components/TokenBalance';

// Logo component with "+" symbol in yellow
function RefStashLogo({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="bg-yellow-300 border-2 border-gray-800 rounded-lg p-2 shadow-lg">
        <svg 
          className="w-6 h-6 text-gray-800 font-bold" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
        </svg>
      </div>
      <span className="text-xl font-bold text-primary" style={{ textShadow: 'none' }}>
        REFSTASH
      </span>
    </div>
  );
}

function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const isAdmin = useQuery(api.publicDirectory.isAdmin);

  const closeMenu = () => setIsOpen(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="dropdown dropdown-end" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-square btn-ghost"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <title>User menu icon</title>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </button>

      {/* User Menu Dropdown */}
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div className="sm:hidden fixed inset-0 bg-black bg-opacity-25 z-40" onClick={closeMenu} />
          
          <div className="dropdown-content menu bg-white rounded-box w-64 sm:w-72 shadow-lg border-2 border-base-300 z-50 mt-2">
            <div className="p-2">
              {/* Token Balance Section */}
              <div className="px-3 py-2 border-b border-base-300 mb-2">
                <div className="text-xs text-gray-600 mb-1">Token Balance</div>
                <TokenBalance showDetails />
              </div>
              
              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    void navigate('/projects');
                  }}
                  className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <title>Projects icon</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  Projects
                </button>
              </li>

              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    void navigate('/tag-cloud');
                  }}
                  className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <title>Tag cloud icon</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Tag Cloud
                </button>
              </li>
              
               <li>
                 <button
                   type="button"
                   onClick={() => {
                     closeMenu();
                     void navigate('/saved-prompts');
                   }}
                   className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                 >
                   <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                     <title>Saved prompts icon</title>
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                   </svg>
                   Saved Prompts
                 </button>
               </li>

               <li>
                 <button
                   type="button"
                   onClick={() => {
                     closeMenu();
                     void navigate('/public');
                   }}
                   className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                 >
                   <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                     <title>Public directory icon</title>
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                   </svg>
                   Public Directory
                 </button>
               </li>
              
              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    void navigate('/tokens');
                  }}
                  className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <title>Token management icon</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  Token Management
                </button>
              </li>

              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu();
                    void navigate('/settings');
                  }}
                  className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <title>Settings icon</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Settings (API Tokens)
                </button>
              </li>
              
              {/* Only show debug link in development */}
              {import.meta.env.DEV && (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      closeMenu();
                      void navigate('/debug');
                    }}
                    className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <title>Debug icon</title>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Debug Logs
                  </button>
                </li>
              )}
              
              {/* Admin moderation link - only show for admins */}
              {isAdmin === true && (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      closeMenu();
                      void navigate('/admin/moderation');
                    }}
                    className="flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg p-2 w-full"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <title>Moderation icon</title>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Moderation Queue
                  </button>
                </li>
              )}
              
              <div className="divider my-2"></div>
              
              <li>
                <div className="p-2">
                  <SignOutButton />
                </div>
              </li>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useConvexAuth();

  // Don't show top bar on landing page for unauthenticated users
  if (!isAuthenticated && location.pathname === '/') {
    return null;
  }

  const isAddingReference = location.pathname === '/new-reference';
  const isDebugPage = location.pathname === '/debug';
  const isTagCloudPage = location.pathname === '/tag-cloud';
  const isSavedPromptsPage = location.pathname === '/saved-prompts';
  const isTokenPage = location.pathname.startsWith('/tokens') || location.pathname.startsWith('/admin');

  return (
    <header className="navbar bg-base-100 border-b-2 border-base-300 shadow-lg sticky top-0 z-50">
      {/* Mobile Layout (sm and below) */}
      <div className="navbar-start sm:hidden">
        <Link
          to="/"
          className="btn btn-ghost"
          aria-label="Go to home"
        >
          <RefStashLogo />
        </Link>
      </div>
      
      <div className="navbar-end sm:hidden">
        <div className="flex items-center gap-2">
          {/* Token Balance for mobile */}
          <TokenBalance className="sm:hidden" />
          
          {/* Compact action buttons */}
          {!isAddingReference && !isDebugPage && !isTagCloudPage && !isSavedPromptsPage && !isTokenPage && (
            <>
              <button
                type="button"
                onClick={() => void navigate('/landing')}
                className="btn btn-square btn-outline btn-sm"
                title="Features"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <title>Features icon</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => void navigate('/tag-cloud')}
                className="btn btn-square btn-outline btn-sm"
                title="Tag Cloud"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <title>Tag cloud icon</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </button>
            </>
          )}
          <UserMenu />
        </div>
      </div>

      {/* Desktop Layout (sm and above) */}
      <div className="hidden sm:flex navbar-start">
        <Link
          to="/"
          className="btn btn-ghost"
        >
          <RefStashLogo />
        </Link>
        
        <div className="hidden md:flex ml-8">
          <ul className="menu menu-horizontal px-1 gap-2">
            <li>
              <Link
                to="/"
                className={`btn btn-sm ${
                  location.pathname === '/'
                    ? 'btn-primary'
                    : 'btn-ghost'
                }`}
              >
                References
              </Link>
            </li>
            <li>
              <Link
                to="/projects"
                className={`btn btn-sm ${
                  location.pathname.startsWith('/projects')
                    ? 'btn-primary'
                    : 'btn-ghost'
                }`}
              >
                Projects
              </Link>
            </li>
            <li>
              <Link
                to="/tag-cloud"
                className={`btn btn-sm ${
                  location.pathname === '/tag-cloud'
                    ? 'btn-primary'
                    : 'btn-ghost'
                }`}
              >
                Tag Cloud
              </Link>
            </li>
            <li>
              <Link
                to="/saved-prompts"
                className={`btn btn-sm ${
                  location.pathname === '/saved-prompts' 
                    ? 'btn-primary' 
                    : 'btn-ghost'
                }`}
              >
                Saved Prompts
              </Link>
            </li>
            <li>
              <Link
                to="/public"
                className={`btn btn-sm ${
                  location.pathname === '/public' 
                    ? 'btn-primary' 
                    : 'btn-ghost'
                }`}
              >
                Public Directory
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="hidden sm:flex navbar-end gap-4">
        {/* Add Reference Button */}
        <button
          type="button"
          onClick={() => void navigate('/new-reference')}
          className="btn bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Reference
        </button>

        {/* Token Balance */}
        <TokenBalance />

        {/* User Menu */}
        <UserMenu />
      </div>
    </header>
  );
} 
